// Tiny shared building blocks for sortable tables.
//
// Used by the Client Workspace landing tables (ClientList,
// ClientLanding's scenarios + staff plans) so they all share the same
// header chrome, sort affordances, and "Updated 3d ago" formatting.

import { ArrowDown, ArrowUp } from 'lucide-react';
import clsx from 'clsx';
import { fmtRelative } from '../../lib/format';

export type SortDir = 'asc' | 'desc';

/** Re-export so existing `import { formatRelative } from './tables/SortHeader'`
 *  call sites keep working. New code should pull `fmtRelative` from
 *  `lib/format.ts` directly. */
export const formatRelative = fmtRelative;

export interface SortHeaderProps<K extends string> {
  label: string;
  sortKey: K;
  activeKey: K;
  dir: SortDir;
  onSort: (key: K) => void;
  className?: string;
  /** When true, right-aligns the label and flips the icon to the left so
   *  numeric columns read like a standard table. */
  numeric?: boolean;
}

export function SortHeader<K extends string>({
  label,
  sortKey,
  activeKey,
  dir,
  onSort,
  className,
  numeric,
}: SortHeaderProps<K>) {
  const active = sortKey === activeKey;
  return (
    <th
      className={clsx(
        'py-2.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={clsx(
          'inline-flex items-center gap-1 hover:text-slate-800 transition-colors group',
          numeric && 'flex-row-reverse',
          active && 'text-slate-800',
        )}
      >
        <span>{label}</span>
        <span
          className={clsx(
            'inline-flex w-3 transition-opacity',
            active ? 'opacity-100' : 'opacity-0 group-hover:opacity-50',
          )}
        >
          {dir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
        </span>
      </button>
    </th>
  );
}

